/** biome-ignore-all lint/a11y/noLabelWithoutControl: <explanation> */
import clsx from 'clsx'
import { AlertCircle, UploadIcon } from 'lucide-react'
import {
  type ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useRef
} from 'react'
import type { FieldError } from 'react-hook-form'
import { FileCheckIcon } from '@/assets/icons/check-icon'
import { LoadingPageIcon } from '@/assets/icons/loading-page-icon'
import { StarGroup } from '@/assets/icons/star-group'
import { FloatLabel } from '@/components/float-label'
import { Show } from '@/components/show'
import { Button } from '@/components/ui/button'
import { Input, type InputProps } from '@/components/ui/input'
import type { FileUploadStatus } from '@/types/file-upload/file-upload-status'

type InputFileProps = InputProps & {
  error?: FieldError
  fileName?: string
  status?: FileUploadStatus
  errorMessageAlign?: 'start' | 'end'
  warning?: string
  onRemove?: () => void
}

export const InputFile = forwardRef<HTMLInputElement, InputFileProps>(
  (
    {
      error,
      className,
      onChange,
      placeholder,
      label,
      variant,
      fileName,
      status,
      errorMessageAlign = 'start',
      onRemove,
      warning = 'Não foi possível ler o documento',
      ...props
    },
    ref
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => fileInputRef.current as HTMLInputElement)

    const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
      if (onChange) onChange(event)
    }

    function handleOpenFileDialog() {
      fileInputRef.current?.click()
    }

    const getFileName = () => {
      if (fileName && fileName.length > 0) {
        return fileName
      }
      return placeholder
    }

    function renderStatusIcon() {
      if (status === 'pending')
        return <LoadingPageIcon className="w-5 h-5 animate-spin ml-2 me-60" />
      if (status === 'success')
        return <FileCheckIcon className="ml-2 text-[var(--brand-green)]" />
      if (status === 'error' || status === 'warning')
        return <AlertCircle size={20} className="ml-2 text-destructive me-60" />
      return null
    }

    return (
      <>
        <Show when={status === 'error' || status === 'warning'}>
          <div className="flex justify-end w-full">
            <div className="w-34 h-3 me-62">
              <Button
                type="button"
                variant="link"
                className="h-1"
                onClick={handleOpenFileDialog}
              >
                Substitua o arquivo!
              </Button>
            </div>
          </div>
        </Show>
        <FloatLabel
          label={label}
          variant={variant}
          hasValue={!!fileName?.length}
          classNameLabel="z-10"
        >
          <label
            className={clsx(
              'h-10 mt-4 relative border rounded-md px-3 py-2 text-sm text-muted-foreground cursor-pointer flex flex-col items-center justify-between',
              {
                'border-input': status === 'idle' && !error,
                'border-[var(--brand-green)]': status === 'success',
                'border-destructive text-destructive':
                  status === 'error' || error || status === 'warning'
              }
            )}
          >
            <div className="w-full flex justify-between items-center px-1 mt-0.5">
              <span>{getFileName()}</span>
              {renderStatusIcon()}
            </div>

            <Input
              id="file-upload"
              type="file"
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                event.stopPropagation()
              }}
              ref={fileInputRef}
              onChange={(event) => handleInput(event)}
              className={clsx('hidden', className)}
              {...props}
            />

            <Show when={!error && status === 'idle'}>
              <UploadIcon
                className={'absolute right-5 text-[var(--brand-gray-1)] me-60'}
              />
            </Show>

            <Show when={status === 'error' || status === 'warning'}>
              <div
                className={clsx(
                  'w-full flex mt-3 gap-1 me-120',
                  { 'justify-end': errorMessageAlign === 'end' },
                  { 'justify-end': errorMessageAlign === 'start' }
                )}
              >
                <StarGroup />
                <span className="italic">
                  {error ? error.message : warning}
                </span>
              </div>
            </Show>
          </label>
        </FloatLabel>
      </>
    )
  }
)
InputFile.displayName = 'InputFile'
